import { useEffect, useRef, useState, memo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { REPORT_CONTENT } from '../../services/apollo/mutations/reportMutations';

const AUTO_CLOSE_MS = 2500;
const MAX_DESCRIPTION_LENGTH = 500;

const ReportModal = ({
  isOpen,
  onClose,
  targetType,
  targetId,
  targetLabel,
}) => {
  const { t } = useTranslation();
  const [reportContent] = useMutation(REPORT_CONTENT);

  const [selectedReason, setSelectedReason] = useState(null);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  const reasons = [
    { value: 'spam', label: t('report_reason_spam', 'Spam or repeated unwanted content') },
    { value: 'harassment', label: t('report_reason_harassment', 'Harassment or bullying') },
    { value: 'inappropriate', label: t('report_reason_inappropriate', 'Inappropriate or offensive content') },
    { value: 'scam', label: t('report_reason_scam', 'Scam or fraud') },
    { value: 'misinformation', label: t('report_reason_misinformation', 'False or misleading information') },
    { value: 'animal_welfare', label: t('report_reason_animal_welfare', 'Animal welfare concern') },
    { value: 'other', label: t('report_reason_other', 'Other (please describe)') },
  ];

  const resolvedTargetLabel = targetLabel || t('report_target_default', 'this content');
  const title = t('report_modal_title', {
    defaultValue: 'Report {{targetLabel}}',
    targetLabel: resolvedTargetLabel,
  });

  const trimmedDescription = description.trim();
  const isOtherReason = selectedReason === 'other';
  const isSubmitDisabled =
    submitting ||
    !selectedReason ||
    (isOtherReason && trimmedDescription.length === 0);

  useEffect(() => {
    if (isOpen) {
      setSelectedReason(null);
      setDescription('');
      setSubmitting(false);
      setResult(null);
      previouslyFocusedRef.current =
        typeof document !== 'undefined' ? document.activeElement : null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && dialogRef.current) {
      const id = setTimeout(() => {
        if (dialogRef.current && typeof dialogRef.current.focus === 'function') {
          dialogRef.current.focus();
        }
      }, 0);
      return () => clearTimeout(id);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) return undefined;
    const el = previouslyFocusedRef.current;
    if (!el) return undefined;
    const id = setTimeout(() => {
      if (el && typeof el.focus === 'function') {
        el.focus();
      }
    }, 0);
    previouslyFocusedRef.current = null;
    return () => clearTimeout(id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !submitting) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, submitting, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!result?.success) return undefined;
    const id = setTimeout(() => {
      onClose();
    }, AUTO_CLOSE_MS);
    return () => clearTimeout(id);
  }, [result, onClose]);

  const handleBackdropClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;
    setSubmitting(true);
    try {
      const { data } = await reportContent({
        variables: {
          input: {
            targetType,
            targetId,
            reason: selectedReason,
            description: trimmedDescription || null,
          },
        },
      });
      setResult(
        data?.reportContent || {
          success: false,
          message: t(
            'report_error_generic',
            'Could not submit report. Please try again.'
          ),
        }
      );
    } catch (err) {
      setResult({
        success: false,
        message:
          err?.message ||
          t('report_error_generic', 'Could not submit report. Please try again.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTryAgain = () => {
    setResult(null);
  };

  const handleDescriptionChange = (e) => {
    const next = e.target.value;
    setDescription(
      next.length > MAX_DESCRIPTION_LENGTH
        ? next.slice(0, MAX_DESCRIPTION_LENGTH)
        : next
    );
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm touch-none"
            onClick={handleBackdropClose}
            onWheel={(e) => e.preventDefault()}
            onTouchMove={(e) => e.preventDefault()}
            aria-hidden="true"
          />

          <div
            className="fixed inset-0 z-[111] flex items-center justify-center px-4 sm:px-6"
            onClick={handleBackdropClose}
          >
            <motion.div
              ref={dialogRef}
              tabIndex={-1}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="bg-white w-full max-w-md max-h-[85vh] rounded-2xl overflow-hidden shadow-xl flex flex-col outline-none"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="report-modal-title"
            >

              <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
                <h3
                  id="report-modal-title"
                  className="text-base sm:text-lg font-bold text-foreground pr-2 line-clamp-1"
                >
                  {title}
                </h3>
                <button
                  type="button"
                  onClick={handleBackdropClose}
                  disabled={submitting}
                  aria-label={t('close', 'Close')}
                  className="p-1.5 rounded-lg text-muted-foreground hover:bg-gray-100 hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {result === null && (
                  <div className="p-5 space-y-5">
                    <fieldset disabled={submitting} className="space-y-2">
                      <legend className="sr-only">
                        {t('report_reason_legend', 'Report reason')}
                      </legend>
                      {reasons.map(({ value, label }) => {
                        const id = `report-reason-${value}`;
                        const selected = selectedReason === value;
                        return (
                          <label
                            key={value}
                            htmlFor={id}
                            className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                              selected
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:bg-gray-50'
                            }`}
                          >
                            <input
                              id={id}
                              type="radio"
                              name="report-reason"
                              value={value}
                              checked={selected}
                              onChange={() => setSelectedReason(value)}
                              className="mt-0.5 h-4 w-4 text-primary focus:ring-primary"
                            />
                            <span className="text-sm text-foreground leading-snug">
                              {label}
                            </span>
                          </label>
                        );
                      })}
                    </fieldset>

                    <div className="space-y-1.5">
                      <label
                        htmlFor="report-description"
                        className="block text-sm font-medium text-foreground"
                      >
                        {isOtherReason
                          ? t(
                              'report_description_required',
                              'Please describe (required)'
                            )
                          : t(
                              'report_description_label',
                              'Additional details (optional)'
                            )}
                      </label>
                      <textarea
                        id="report-description"
                        value={description}
                        onChange={handleDescriptionChange}
                        disabled={submitting}
                        maxLength={MAX_DESCRIPTION_LENGTH}
                        rows={4}
                        placeholder={t(
                          'report_description_placeholder',
                          "Tell us more about why you're reporting this..."
                        )}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-border focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <div className="flex justify-end">
                        <span
                          className="text-xs text-muted-foreground tabular-nums"
                          aria-live="polite"
                        >
                          {description.length} / {MAX_DESCRIPTION_LENGTH}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {result?.success === true && (
                  <div className="p-6 text-center space-y-3">
                    <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto">
                      <CheckCircle2 className="w-7 h-7 text-green-600" />
                    </div>
                    <h4 className="text-base font-semibold text-foreground">
                      {t('report_success_title', 'Report submitted')}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {result.message}
                    </p>
                  </div>
                )}

                {result && result.success === false && (
                  <div className="p-6 text-center space-y-3">
                    <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                      <AlertCircle className="w-7 h-7 text-red-500" />
                    </div>
                    <h4 className="text-base font-semibold text-foreground">
                      {t('report_error_title', "Couldn't submit report")}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {result.message}
                    </p>
                    <button
                      type="button"
                      onClick={handleTryAgain}
                      className="mt-2 inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
                    >
                      {t('report_try_again', 'Try Again')}
                    </button>
                  </div>
                )}
              </div>

              {result === null && (
                <div className="flex border-t border-border flex-shrink-0">
                  <button
                    type="button"
                    onClick={handleBackdropClose}
                    disabled={submitting}
                    className="flex-1 py-4 text-sm font-semibold text-foreground hover:bg-gray-50 transition-colors border-r border-border disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('report_cancel', 'Cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitDisabled}
                    aria-disabled={isSubmitDisabled}
                    className="flex-1 py-4 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('report_submitting', 'Submitting...')}
                      </>
                    ) : (
                      t('report_submit', 'Submit Report')
                    )}
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default memo(ReportModal);
